/**
 * src/components/spatial/useSpeechInput.ts
 *
 * Talking to a building instead of typing at it.
 *
 * There was no speech recognition anywhere in this repo before this file.
 * Everything named "speech" was the other direction: `agentSpeechService`
 * synthesises through Azure on the server, and `getUserMedia` appears only
 * in the human live-broadcast flow, where it feeds a video stream and is
 * never transcribed. So this is new, and being new it is deliberately the
 * smallest thing that works.
 *
 * WHY THE BROWSER AND NOT AZURE. The Azure Speech SDK is a `server`
 * dependency, not a client one, and it needs `TextEncoder` — which is why
 * anything importing it runs under the node test environment. Using it for
 * recognition would mean recording audio, uploading it, and waiting for a
 * round trip before the visitor saw a word. `SpeechRecognition` is in the
 * browser, costs nothing, and streams interim results, which is what makes
 * the transcript watchable while it is being spoken. Firefox does not have
 * it; there the control is hidden rather than shown broken, and a server
 * route stays available as a later option.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not send. A final result lands
 * in the composer and stops there, editable, waiting for the visitor to press
 * Send exactly as they would after typing. Auto-sending is how a
 * recognition error becomes a message nobody chose to say — and recognition
 * errors are the normal case for place names, wallet addresses and anything
 * said near traffic. Hands-free conversation is a separate piece of work with
 * its own decisions about end-of-speech and confirmation.
 *
 * It also creates no session, no history and no second conversation. The
 * transcript is text in an input; everything after that is the path a typed
 * message already takes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { normaliseCaptureError } from '../../services/mediaDevices';

/**
 * The parts of the Web Speech API this uses.
 *
 * Declared locally because `SpeechRecognition` is not in this project's DOM
 * typings — it is a draft API, and Chrome exposes it under a vendor prefix.
 * Narrow on purpose: the surface below is what the hook touches, so a
 * mismatch shows up here rather than as `any` spreading outward.
 */
interface RecognitionAlternative { transcript: string }
interface RecognitionResult {
  readonly length: number;
  readonly isFinal: boolean;
  [index: number]: RecognitionAlternative;
}
interface RecognitionResultList {
  readonly length: number;
  [index: number]: RecognitionResult;
}
interface RecognitionEvent {
  resultIndex: number;
  results: RecognitionResultList;
}
interface RecognitionErrorEvent { error: string }

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

interface SpeechWindow {
  SpeechRecognition?: RecognitionConstructor;
  webkitSpeechRecognition?: RecognitionConstructor;
}

/** The constructor, whichever name this browser uses for it. */
function _constructor(): RecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as SpeechWindow;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/**
 * Recognition errors, said in words a visitor can act on.
 *
 * `not-allowed` and `service-not-allowed` are permission, so they go through
 * `normaliseCaptureError` — the same taxonomy the live-broadcast flow uses
 * for a refused camera or microphone, rather than a second vocabulary for the
 * same refusals. The rest are recognition-specific and have no equivalent
 * there.
 */
function _messageFor(code: string): string {
  if (code === 'not-allowed' || code === 'service-not-allowed') {
    return normaliseCaptureError({ name: 'NotAllowedError', message: 'permission denied' }).message;
  }
  if (code === 'no-speech') return 'I did not hear anything.';
  if (code === 'audio-capture') {
    return normaliseCaptureError({ name: 'NotFoundError', message: 'not found' }).message;
  }
  if (code === 'network') return 'Speech recognition could not reach the network.';
  if (code === 'aborted') return '';
  return 'Speech recognition stopped unexpectedly.';
}

export interface SpeechInput {
  /** Whether this browser can do it at all. False hides the control. */
  supported: boolean;
  /** Whether the microphone is open right now. */
  listening: boolean;
  /** The last error, in words, or null. Empty-string errors are dropped. */
  error: string | null;
  /** Open the microphone. Safe to call when already listening. */
  start: () => void;
  /** Close it. Whatever was recognised has already been delivered. */
  stop: () => void;
}

/**
 * Listen, and hand every transcript to `onTranscript`.
 *
 * `onTranscript` receives the text and whether it is final. Both are
 * delivered: interim results are what make the composer fill in as somebody
 * speaks, and a caller that only wants the finished sentence can ignore the
 * rest. Nothing here decides what to do with it — in particular, nothing
 * here sends.
 *
 * `onBeforeStart` runs immediately before the microphone opens, and exists
 * for barge-in: the caller stops the object speaking, if the object's own
 * `interruptMode` says it should. It lives here rather than at the call site
 * so the ordering is guaranteed — stopping playback after capture has begun
 * means the first words are spoken over.
 */
export function useSpeechInput(input: {
  onTranscript: (text: string, isFinal: boolean) => void;
  onBeforeStart?: () => void;
  /** BCP-47 tag. Defaults to whatever the browser is set to. */
  lang?: string;
}): SpeechInput {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  // Held in refs so a caller passing a fresh closure every render does not
  // tear down and rebuild the recogniser mid-sentence.
  const onTranscriptRef = useRef(input.onTranscript);
  onTranscriptRef.current = input.onTranscript;
  const onBeforeStartRef = useRef(input.onBeforeStart);
  onBeforeStartRef.current = input.onBeforeStart;

  const [supported] = useState(() => _constructor() !== null);

  /** Let go of the microphone on unmount. */
  useEffect(() => () => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    // `abort` rather than `stop`: the component is going away, so a final
    // result arriving afterwards has nowhere to land.
    try { recognition?.abort(); } catch { /* already gone */ }
  }, []);

  const start = useCallback(() => {
    if (recognitionRef.current) return; // already listening

    const Recognition = _constructor();
    if (!Recognition) {
      setSupportedError(setError);
      return;
    }

    // Before the microphone opens, not after.
    try { onBeforeStartRef.current?.(); } catch { /* never block listening */ }

    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Recognition();
    } catch {
      setError('Speech recognition could not start.');
      return;
    }

    if (input.lang) recognition.lang = input.lang;
    // One utterance at a time. `continuous` would keep the microphone open
    // indefinitely, which is both a battery cost and a thing a visitor cannot
    // see the end of.
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      // Only the results from this batch onward: `resultIndex` is where the
      // new ones begin, and re-reading from zero would repeat text the caller
      // has already been given.
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result || result.length === 0) continue;
        const transcript = result[0]?.transcript ?? '';
        if (!transcript) continue;
        // Delivered, not sent. What the caller does with a final result is
        // the caller's business, and in this codebase it is to fill in the
        // composer and wait for the visitor.
        onTranscriptRef.current(transcript, !!result.isFinal);
      }
    };

    recognition.onerror = (event) => {
      const message = _messageFor(event?.error || '');
      // An abort is something we asked for, so it is not news.
      if (message) setError(message);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      // Chrome throws if `start` is called while a previous session is still
      // winding down. Nothing is broken and nothing needs saying.
      recognitionRef.current = null;
      setListening(false);
    }
  }, [input.lang]);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    // `stop` rather than `abort`: a sentence half-spoken should still be
    // delivered, because the visitor can see it and edit it.
    try { recognition.stop(); } catch { /* already stopping */ }
  }, []);

  return { supported, listening, error, start, stop };
}

/** Kept out of the callback so the message lives in one place. */
function setSupportedError(setError: (value: string) => void): void {
  setError('This browser cannot listen. You can still type.');
}

export default useSpeechInput;
